import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import {
  CheckCircle2,
  Clock3,
  Download,
  FileSpreadsheet,
  FileText,
  Mail,
  Receipt,
  ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonTableRows } from '../ui/kit';

const STATE_LABELS = {
  calculated: 'Berechnet',
  ready: 'Freigegeben',
  exported: 'An Bankdatei übergeben',
  paid: 'Ausgezahlt',
};

const cents = (value) => `${(Number(value || 0) / 100).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;

// PARTNER-LEISTUNG-EINNAHMEN-UBER-001: `carry_out_cents` wird als POSITIVE Schuld
// gespeichert (backend/src/lib/financeLedger.js:1838) und kommt in der Folgewoche als
// NEGATIVER `carry_in` zurück (:1835). Der Schlusssaldo ist deshalb −carry_out — sonst
// stünde derselbe Betrag auf zwei Bildschirmen mit verschiedenem Vorzeichen. Die 0 wird
// angezeigt, nicht unterdrückt; `-0` würde sonst als „-0,00 €" erscheinen.
const gedreht = (value) => ((Number(value) || 0) === 0 ? 0 : -Number(value));

// Name des Dokuments im Bestätigungsschritt, damit klar ist, was nach dem Code gesendet wird.
const dokumentName = (art, nummer, zeitraum) => [art, nummer, zeitraum && `(${zeitraum})`].filter(Boolean).join(' ');

function saveBlob(response, filename, type) {
  const url = window.URL.createObjectURL(new Blob([response.data], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export default function Invoices() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailCheck, setEmailCheck] = useState(null);

  useEffect(() => {
    api.get('/partners/settlements')
      .then((response) => setPeriods(response.data.periods || []))
      .catch(() => toast.error('Abrechnungshistorie konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  const download = async (period, path, filename, type = 'application/pdf') => {
    try {
      const response = await api.get(`/partners/payouts/${period.id}/${path}`, { responseType: 'blob' });
      saveBlob(response, filename, type);
    } catch (error) {
      toast.error('Datei konnte nicht geladen werden');
    }
  };

  const emailDocument = async (documentId, label = 'das Dokument') => {
    try {
      await api.post(`/finance/documents/${documentId}/email`);
      toast.success('Sicherer Link wurde an die verifizierte Konto-E-Mail gesendet');
    } catch (error) {
      // PARTNER-RECHNUNG-MAIL-BESTAETIGEN-001: Der Versand verlangt users.email_verified = 1
      // (backend/src/routes/finance.js verifiedRecipient). Partnerkonten entstehen mit 0
      // (routes/auth.js /register ohne signup_token), und das Portal bot bisher keinen Weg,
      // das zu ändern — die Meldung war eine Sackgasse. Jetzt startet hier der vorhandene
      // Code-Weg /auth/me/email/request + /confirm; danach wird das Dokument erneut gemailt.
      // Ein Klick auf ein anderes Dokument bei offenem Schritt wechselt nur das Ziel; ein
      // bereits angeforderter Code und die Eingabe bleiben stehen. Während der laufenden
      // Bestätigung bleibt das Ziel fest — gesendet wird immer genau das angezeigte Dokument.
      // Beim Code-Anfordern ist der Wechsel frei: das Ziel wird erst beim Bestätigen festgehalten.
      if (error.response?.data?.error === 'email_not_verified') {
        setEmailCheck((check) => {
          if (!check) return { documentId, label, seq: 1, step: 'start', code: '', busy: false, hint: '' };
          if (check.busy === 'confirm') return { ...check, seq: check.seq + 1 };
          return { ...check, documentId, label, seq: check.seq + 1 };
        });
        return;
      }
      toast.error('E-Mail-Link konnte nicht gesendet werden');
    }
  };

  // Das Panel steht über der Tabelle. Wer in einer tieferen Zeile klickt, sähe sonst keine
  // Reaktion — deshalb bei jedem Öffnen bzw. Zielwechsel hinscrollen und den nächsten Schritt fokussieren.
  const emailCheckRef = useRef(null);
  const emailCheckSeq = emailCheck?.seq;
  useEffect(() => {
    const panel = emailCheckRef.current;
    if (!emailCheckSeq || !panel) return;
    if (typeof panel.scrollIntoView === 'function') panel.scrollIntoView({ block: 'center', behavior: 'smooth' });
    panel.querySelector('[data-email-check-focus]')?.focus({ preventScroll: true });
  }, [emailCheckSeq]);

  const requestEmailCode = async () => {
    setEmailCheck((check) => check && { ...check, busy: 'request', hint: '' });
    try {
      const response = await api.post('/auth/me/email/request');
      const target = typeof response.data?.email === 'string' ? response.data.email : 'deine Konto-E-Mail';
      setEmailCheck((check) => check && { ...check, step: 'code', busy: false, seq: check.seq + 1, hint: `Code gesendet an ${target}.` });
    } catch (error) {
      const data = error.response?.data || {};
      const status = error.response?.status;
      if (data.error === 'code_already_sent') {
        setEmailCheck((check) => check && { ...check, step: 'code', busy: false, seq: check.seq + 1, hint: 'Ein Code ist schon unterwegs. Bitte schau in dein Postfach.' });
        return;
      }
      const hint = status === 429 ? 'Zu viele Anfragen. Bitte versuche es in ein paar Minuten erneut.'
        : data.error === 'email_link_required' ? 'Die Bestätigung geht hier nicht. Bitte wende dich an den Support.'
          : (typeof data.message === 'string' && data.message) || 'Der Code konnte nicht gesendet werden. Bitte versuche es später erneut.';
      setEmailCheck((check) => check && { ...check, busy: false, hint });
    }
  };

  const confirmEmailCode = async (event) => {
    event.preventDefault();
    if (!emailCheck || emailCheck.busy) return;
    const { documentId, label, code } = emailCheck;
    setEmailCheck((check) => check && { ...check, busy: 'confirm', hint: '' });
    try {
      await api.post('/auth/me/email/confirm', { code: code.trim() });
    } catch (error) {
      // Nur eine 400 heißt „falscher Code" (auth.js /me/email/confirm). Netz- und Serverfehler
      // dürfen den Partner nicht an einem richtigen Code zweifeln lassen.
      const message = error.response?.data?.message;
      const hint = error.response?.status === 400
        ? (typeof message === 'string' && message) || 'Code ungültig oder abgelaufen.'
        : 'Die Bestätigung hat gerade nicht geklappt. Bitte versuche es gleich noch einmal.';
      setEmailCheck((check) => check && { ...check, busy: false, hint });
      return;
    }
    setEmailCheck(null);
    toast.success('Konto-E-Mail bestätigt');
    await emailDocument(documentId, label);
  };

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#9C27B0', fontSize: 12, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase' }}>
          <ShieldCheck size={13} /> Unveränderliche Finanzstände
        </span>
        <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -0.7, margin: '6px 0' }}>Abrechnung</h1>
        <p style={{ color: '#4b5563', fontSize: 13, margin: 0 }}>
          Umsatz- und Auszahlungsübersicht, separate VOOGO-Gebührenrechnung und Partnerdetail-CSV.
        </p>
      </div>

      <div style={{ border: '1px solid #374151', borderRadius: 12, background: '#0a0a0a', padding: 14, marginBottom: 18, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Receipt size={17} color="#9C27B0" />
        <div>
          <strong style={{ color: '#f3f4f6', fontSize: 13 }}>Zwei getrennte Dokumente</strong>
          <p style={{ color: '#d1d5db', fontSize: 12, lineHeight: 1.55, margin: '3px 0 0' }}>
            Die Umsatz- und Auszahlungsübersicht ist ausdrücklich keine Rechnung. Die Rechnung betrifft nur die eigenen VOOGO-Leistungen; Zahlungsdienstleisterkosten sind neutral und separat ausgewiesen.
          </p>
        </div>
      </div>

      {emailCheck && (
        <section ref={emailCheckRef} aria-label="Konto-E-Mail bestätigen" data-testid="email-check" style={{ border: '1px solid #9C27B0', borderRadius: 12, background: '#0a0a0a', padding: 14, marginBottom: 18, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Mail size={17} color="#9C27B0" />
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#f3f4f6', fontSize: 13 }}>Konto-E-Mail bestätigen</strong>
            <p style={{ color: '#d1d5db', fontSize: 12, lineHeight: 1.55, margin: '3px 0 10px' }}>
              Dokumente schicken wir nur an eine bestätigte Konto-E-Mail. Das geht einmalig mit einem Code, den wir dir per E-Mail senden. Danach schicken wir das Dokument sofort. Herunterladen kannst du die Dokumente auch ohne E-Mail über „Übersicht“ und „Gebührenrechnung“.
            </p>
            <p data-testid="email-check-target" style={{ color: '#f3f4f6', fontSize: 12, margin: '0 0 10px' }}>
              Danach senden wir: {emailCheck.label}
            </p>
            {emailCheck.step === 'code' && (
              <form onSubmit={confirmEmailCode} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <input
                  data-email-check-focus
                  aria-label="Code aus der E-Mail"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={8}
                  value={emailCheck.code}
                  onChange={(event) => { const code = event.target.value; setEmailCheck((check) => check && { ...check, code }); }}
                  style={{ padding: '7px 10px', borderRadius: 7, border: '1px solid #4b5563', background: '#111827', color: '#f3f4f6', fontSize: 13, width: 140 }}
                />
                <button type="submit" disabled={Boolean(emailCheck.busy) || !emailCheck.code.trim()} style={buttonStyle}>
                  Bestätigen und senden
                </button>
              </form>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <button type="button" onClick={requestEmailCode} disabled={Boolean(emailCheck.busy)} style={buttonStyle}
                {...(emailCheck.step === 'code' ? {} : { 'data-email-check-focus': true })}>
                {emailCheck.step === 'code' ? 'Code erneut senden' : 'Code senden'}
              </button>
              <button type="button" onClick={() => setEmailCheck(null)} disabled={Boolean(emailCheck.busy)} style={buttonStyle}>
                Abbrechen
              </button>
            </div>
            {emailCheck.hint && <p role="status" style={{ color: '#d1d5db', fontSize: 12, margin: '8px 0 0' }}>{emailCheck.hint}</p>}
          </div>
        </section>
      )}

      <div className="pp-dark-finance-table" style={{ overflowX: 'auto', border: '1px solid #374151', borderRadius: 12 }}>
        <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', background: '#0a0a0a' }}>
          {/* PARTNER-LEISTUNG-EINNAHMEN-UBER-001 (P1-1): Die Spaltenköpfe heißen wortgleich
              wie der verbindliche Beleg (backend/src/lib/financeDocuments.js:212-214,238)
              UND wie die Einnahmen-Seite. „Kartengebühren" hieß dieselbe Zahl nur hier —
              und war zu eng, weil das Feld auch die Kosten aus Guthabenkäufen enthält
              (financeDocuments.js:228-231). `data-spalte` trägt den reinen Namen, damit
              die Erklärzeile den Kopf nicht verlängert. */}
          <thead><tr style={{ textAlign: 'left' }}>
            {['Zeitraum', 'Fahrten', 'Bar', 'Online', 'Auszahlungsbetrag', 'Schlusssaldo', 'Zahlungsdienstleisterkosten', 'Status', 'Dokumente'].map((heading) => (
              <th key={heading} data-spalte={heading} style={{ padding: '12px 14px', fontSize: 12, color: '#d1d5db', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {heading}
                {heading === 'Zahlungsdienstleisterkosten' && (
                  <small style={{ display: 'block', marginTop: 4, fontSize: 11, fontWeight: 600, color: '#d1d5db', textTransform: 'none', letterSpacing: 0 }}>
                    Was der Zahlungsdienstleister für den Einzug berechnet.
                  </small>
                )}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {loading && <SkeletonTableRows rows={4} cols={9} />}
            {!loading && periods.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 42, textAlign: 'center', color: '#d1d5db' }}>Noch keine freigegebenen Wochenabrechnungen.</td></tr>
            )}
            {!loading && periods.map((period) => {
              const paid = period.state === 'paid';
              const providerCosts = period.payment_provider_costs;
              const providerCostCents = providerCosts?.total_cents ?? period.psp_cost_cents;
              return (
                <tr key={period.id} style={{ borderTop: '1px solid #374151' }}>
                  <td style={{ padding: 14, fontSize: 12, color: '#d1d5db' }}>
                    <strong style={{ display: 'block', color: '#f3f4f6' }}>{period.period_label}</strong>
                    <small style={{ display: 'block', color: '#d1d5db' }}>Europe/Berlin</small>
                  </td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{period.ride_count}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{cents(period.cash_cents)}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{cents(period.online_cents)}</td>
                  <td style={{ padding: 14, fontSize: 16, color: '#d8b4e2', fontWeight: 900 }}>{cents(period.payout_cents)}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>{cents(gedreht(period.carry_out_cents))}</td>
                  <td style={{ padding: 14, color: '#d1d5db', fontSize: 13 }}>
                    {cents(gedreht(providerCostCents))}
                    {providerCosts && <>
                      <small style={{ display: 'block' }}>Karte/Stripe {providerCosts.stripe?.transaction_count || 0} · {cents(gedreht(providerCosts.stripe?.fee_cents))}</small>
                      <small style={{ display: 'block' }}>PayPal {providerCosts.paypal?.transaction_count || 0} · {cents(gedreht(providerCosts.paypal?.fee_cents))}</small>
                    </>}
                  </td>
                  <td style={{ padding: 14 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 800, color: paid ? '#d8b4e2' : '#d1d5db', background: paid ? '#24142a' : '#1f2937', borderRadius: 20, padding: '4px 9px' }}>
                      {paid ? <CheckCircle2 size={11} /> : <Clock3 size={11} />} {STATE_LABELS[period.state] || period.state}
                    </span>
                    {period.batch_id && <small style={{ display: 'block', color: '#d1d5db', marginTop: 5 }}>Batch {period.batch_id}</small>}
                  </td>
                  <td style={{ padding: 14 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button type="button" onClick={() => download(period, 'statement', `Umsatzuebersicht-${period.statement_number}.pdf`)} style={buttonStyle}>
                        <Receipt size={12} /> Übersicht
                      </button>
                      <button type="button" onClick={() => download(period, 'invoice', `VOOGO-Gebuehrenrechnung-${period.fee_invoice_number}.pdf`)} style={buttonStyle}>
                        <FileText size={12} /> Gebührenrechnung
                      </button>
                      <button type="button" onClick={() => download(period, 'detail.csv', `Partnerdetail-${period.id}.csv`, 'text/csv;charset=utf-8')} style={buttonStyle}>
                        <FileSpreadsheet size={12} /> CSV
                      </button>
                      <button type="button" onClick={() => emailDocument(period.statement_document_id, dokumentName('Umsatz- und Auszahlungsübersicht', period.statement_number, period.period_label))} style={buttonStyle}>
                        <Mail size={12} /> Übersicht mailen
                      </button>
                      <button type="button" onClick={() => emailDocument(period.fee_invoice_document_id, dokumentName('VOOGO-Gebührenrechnung', period.fee_invoice_number, period.period_label))} style={buttonStyle}>
                        <Mail size={12} /> Rechnung mailen
                      </button>
                    </div>
                    <small style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 7, color: '#d1d5db' }}>
                      <Download size={10} /> Zugriff wird revisionssicher protokolliert
                    </small>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const buttonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '7px 10px',
  background: '#1f2937',
  color: '#f3f4f6',
  border: '1px solid #4b5563',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};
